import { useCallback, useEffect, useRef } from 'react'

import { TOUCH_DRAG_TOLERANCE } from './touchDrag.constants'

type TouchStartPosition = { x: number; y: number }

type UseTouchMovementOptions = {
  onMovementExceedsTolerance?: () => void
  tolerance?: number
  enabled?: boolean
}

export const useTouchMovement = ({
  onMovementExceedsTolerance,
  tolerance = TOUCH_DRAG_TOLERANCE,
  enabled = true
}: UseTouchMovementOptions = {}) => {
  const touchStartPosRef = useRef<TouchStartPosition | null>(null)
  const enabledRef = useRef(enabled)

  useEffect(() => {
    enabledRef.current = enabled
  }, [enabled])

  const reset = useCallback(() => {
    touchStartPosRef.current = null
  }, [])

  const handleTouchStart = useCallback((event: React.TouchEvent) => {
    if (!enabledRef.current) return

    const touch = event.touches[0]
    if (!touch) return

    touchStartPosRef.current = { x: touch.clientX, y: touch.clientY }
  }, [])

  const handleTouchMove = useCallback(
    (event: React.TouchEvent) => {
      if (!enabledRef.current) return
      if (!touchStartPosRef.current) return

      const touch = event.touches[0]
      if (!touch) {
        reset()
        return
      }

      const deltaX = touch.clientX - touchStartPosRef.current.x
      const deltaY = touch.clientY - touchStartPosRef.current.y
      const distance = Math.hypot(deltaX, deltaY)

      if (distance > tolerance && onMovementExceedsTolerance) {
        onMovementExceedsTolerance()
      }
    },
    [tolerance, onMovementExceedsTolerance, reset]
  )

  const handleTouchEnd = useCallback(() => {
    if (!enabledRef.current) return
    reset()
  }, [reset])

  const handleTouchCancel = useCallback(() => {
    if (!enabledRef.current) return
    reset()
  }, [reset])

  return {
    handleTouchStart,
    handleTouchMove,
    handleTouchEnd,
    handleTouchCancel,
    reset,
    touchStartPosition: touchStartPosRef.current
  }
}
